import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X } from '@/components/ui/icons';
import { cn } from '@/lib/utils';
import { useResponsive, getFocusClasses, getTransitionClasses, getTouchAccessibilityClasses } from '@/lib/responsive-utils';
import { trapFocus, handleArrowKeyNavigation } from '@/lib/accessibility';

interface NavigationItem {
  label: string;
  href: string;
  isExternal?: boolean;
  isHighlighted?: boolean;
}

interface HeaderProps {
  className?: string;
}

const navigationItems: NavigationItem[] = [
  { label: 'INICIO', href: '#inicio' },
  { label: 'NOSOTROS', href: '#nosotros' },
  { label: 'SERVICIOS', href: '#servicios' },
  { label: 'PROYECTOS', href: '#proyectos' },
  { label: 'EDUCALIEC', href: '/educaliec' },
  { label: 'COTIZA AQUÍ', href: '#cotiza', isHighlighted: true },
];

export const Header: React.FC<HeaderProps> = ({ className }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { isMobile } = useResponsive();
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const navigationRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const navigate = useNavigate();

  // Handle scroll effect for header styling
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when clicking outside or on navigation item
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const menuButton = document.querySelector('.mobile-menu-button');
      
      // Don't close if clicking the menu button (it has its own toggle)
      if (menuButton?.contains(target)) {
        return;
      }
      
      // Close if clicking outside the menu drawer
      if (isMobileMenuOpen && !target.closest('.mobile-menu-drawer')) {
        setIsMobileMenuOpen(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      // Add a small delay before attaching the click listener
      // to prevent immediate closure
      const timeoutId = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 100);
      
      document.addEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
      
      // Trap focus in mobile menu
      if (mobileMenuRef.current) {
        const cleanup = trapFocus(mobileMenuRef.current);
        return () => {
          clearTimeout(timeoutId);
          cleanup();
          document.removeEventListener('click', handleClickOutside);
          document.removeEventListener('keydown', handleEscapeKey);
          document.body.style.overflow = 'unset';
        };
      }
      
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('click', handleClickOutside);
        document.removeEventListener('keydown', handleEscapeKey);
        document.body.style.overflow = 'unset';
      };
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  // Smooth scrolling function
  const handleSmoothScroll = (href: string) => {
    if (href.startsWith('/')) {
      // Route navigation (e.g., /educaliec) — scroll to top
      navigate(href);
      window.scrollTo({ top: 0 });
      setIsMobileMenuOpen(false);
      return;
    }

    if (href.startsWith('#')) {
      const targetId = href.substring(1);
      const targetElement = document.getElementById(targetId);
      
      if (targetElement) {
        // Element exists on current page — scroll to it
        const headerHeight = 80;
        const targetPosition = targetElement.offsetTop - headerHeight;
        
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
      } else {
        // Element not on current page — navigate to home with hash
        navigate('/' + href);
      }
    }
    
    // Close mobile menu after navigation
    setIsMobileMenuOpen(false);
  };

  const handleNavigationKeyDown = (e: KeyboardEvent, index: number) => {
    const buttons = navigationRefs.current.filter(Boolean) as HTMLButtonElement[];
    handleArrowKeyNavigation(e, buttons, index, () => {});
  };

  const handleNavigationClick = (item: NavigationItem) => {
    if (item.isExternal) {
      window.open(item.href, '_blank', 'noopener,noreferrer');
    } else {
      handleSmoothScroll(item.href);
    }
  };

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0',
        getTransitionClasses(),
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-md' 
          : 'bg-white/90 backdrop-blur-sm',
        getTouchAccessibilityClasses(),
        className
      )}
      style={{ zIndex: 10000 }}
      role="banner"
    >
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20 lg:h-24">
          {/* Logo */}
          <div className="flex-shrink-0">
            <a 
              href="#inicio" 
              onClick={(e) => {
                e.preventDefault();
                handleSmoothScroll('#inicio');
              }}
              className={cn(
                'flex items-center space-x-2 group',
                getFocusClasses(),
                ' p-1'
              )}
            >
              {/* LIEC Logo */}
              <img 
                src="/liec-logo.png" 
                alt="LIEC - Laboratorio de Control de Calidad de Materiales" 
                className="h-10 sm:h-12 lg:h-14 w-auto object-contain transition-all duration-300 ease-in-out group-hover:scale-105"
              />
            </a>
          </div>

          {/* Desktop Navigation */}
          <nav 
            className="hidden lg:flex items-center space-x-6 xl:space-x-8"
            role="navigation"
            aria-label="Main navigation"
          >
            {navigationItems.map((item, index) => (
              <button
                key={item.label}
                ref={(el) => { navigationRefs.current[index] = el; }}
                onClick={() => handleNavigationClick(item)}
                onKeyDown={(e) => handleNavigationKeyDown(e as any, index)}
                className={cn(
                  'text-sm font-medium relative group',
                  getTransitionClasses(),
                  
                  item.isHighlighted
                    ? 'bg-liec-primary-500 text-white px-4 sm:px-6 py-2 sm:py-2.5 hover:bg-liec-primary-600 shadow-md hover:shadow-lg transform hover:-translate-y-0.5'
                    : 'text-liec-neutral-700 hover:text-liec-primary-500 py-2 px-2'
                )}
                aria-label={item.isHighlighted ? `${item.label} (highlighted)` : item.label}
                aria-current={item.href === window.location.hash ? 'page' : undefined}
              >
                {item.label}
                {!item.isHighlighted && (
                  <span className={cn(
                    'absolute bottom-0 left-2 w-0 h-0.5 bg-liec-primary-500',
                    getTransitionClasses(),
                    'group-hover:w-[calc(100%-1rem)]'
                  )} />
                )}
              </button>
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <div className="lg:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                setIsMobileMenuOpen(!isMobileMenuOpen);
              }}
              className={cn(
                'mobile-menu-button  text-liec-neutral-700 hover:text-liec-primary-500 hover:bg-liec-primary-50',
                getFocusClasses(),
                'w-10 h-10 sm:w-12 sm:h-12'
              )}
              aria-label="Toggle mobile menu"
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <X size={isMobile ? 20 : 24} /> : <Menu size={isMobile ? 20 : 24} />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 lg:hidden"
          style={{ zIndex: 10001 }}
          aria-hidden="true"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </div>
      )}

      {/* Mobile Menu Drawer */}
      <div
        ref={mobileMenuRef}
        className={cn(
          'mobile-menu-drawer fixed top-0 right-0 h-full bg-white shadow-2xl lg:hidden',
          getTransitionClasses(),
          'transform',
          isMobileMenuOpen ? 'translate-x-0 ' : 'translate-x-full',
          isMobile ? 'w-full max-w-sm' : 'w-80 max-w-[85vw] '
        )}
        style={{ zIndex: 10002 }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mobile-menu-title"
      >
        <div className="flex flex-col h-full ">
          {/* Mobile Menu Header */}
          <div className="flex items-center bg-white justify-between p-6 border-b border-liec-neutral-200">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-liec-primary-500 rounded-lg flex items-center justify-center">
                <img 
                src="/liec-logo.png" 
                alt="LIEC - Laboratorio de Control de Calidad de Materiales" 
                className="h-10 sm:h-12 lg:h-14 w-auto object-contain transition-all duration-300 ease-in-out group-hover:scale-105"
              />
              </div>
              <div>
                <div 
                  id="mobile-menu-title"
                  className="text-liec-neutral-900 font-bold text-lg leading-tight"
                >
                  LIEC
                </div>
                <div className="text-liec-neutral-600 text-xs leading-tight">
                  Laboratorio de Ingeniería
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMobileMenuOpen(false)}
              className="text-liec-neutral-700 hover:text-liec-primary-500 hover:bg-liec-primary-50"
              aria-label="Close mobile menu"
            >
              <X size={24} />
            </Button>
          </div>

          {/* Mobile Menu Navigation */}
          <nav 
            className="flex-1 px-6 py-8 bg-white"
            role="navigation"
            aria-label="Mobile navigation"
          >
            <div className="space-y-4" role="list">
              {navigationItems.map((item) => (
                <button
                  key={item.label}
                  onClick={() => handleNavigationClick(item)}
                  className={cn(
                    'w-full text-left px-4 py-3  font-medium transition-all duration-200',
                    item.isHighlighted
                      ? 'bg-liec-primary-500 text-white hover:bg-liec-primary-600 shadow-md'
                      : 'text-liec-neutral-700 hover:text-liec-primary-500 hover:bg-liec-primary-50'
                  )}
                  role="listitem"
                  aria-label={item.isHighlighted ? `${item.label} (highlighted)` : item.label}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </nav>

          {/* Mobile Menu Footer 
          <div className="p-6 border-t border-liec-neutral-200">
            <div className="text-center text-sm text-liec-neutral-500">
              © 2026 LIEC - Laboratorio de Control de Calidad de Materiales
            </div>
          </div> */}
        </div>
      </div>
    </header>
  );
};

export default Header;